// Yandex Metrica — web (window.ym counter) and native (AppMetrica SDK via the
// MetricaNative Capacitor plugin). Ports modules/metrica.js for web; native routes
// through the plugin (Phase 3). Native calls are no-ops until init() activates the
// SDK with the counter key (NATIVE_METRIKA_ID — verified at the creds/dashboard step).

import { registerPlugin } from '@capacitor/core'
import { isNative, NATIVE_METRIKA_ID } from './platform'

// --- native bridge (app-local plugin; inert proxy on web, only called under isNative) ---
interface MetricaNativePlugin {
  init(options: { apiKey: string }): Promise<void>
  trackEvent(options: { name: string; params?: Record<string, unknown> }): Promise<void>
  setUserId(options: { userId: string }): Promise<void>
  resetUserId(): Promise<void>
}
const MetricaNative = registerPlugin<MetricaNativePlugin>('MetricaNative')

// --- web counter (window.ym + window.METRIKA_CONFIG injected in index.html at deploy) ---
function cfg(): Partial<MetrikaConfig> {
  return (typeof window !== 'undefined' && window.METRIKA_CONFIG) || {}
}

function ym(...args: unknown[]): void {
  if (typeof window !== 'undefined' && typeof window.ym === 'function') window.ym(...args)
}

/**
 * Resolve the Yandex ClientID (`_ym_uid`) via the tag's own getClientID method.
 *
 * Unlike reading document.cookie directly, this is resilient to the async-set
 * race (the tag hasn't written `_ym_uid` yet at read time) and to ITP cookie
 * restrictions (the tag can serve the ClientID even when the cookie is
 * constrained). Returns the same value Direct matches conversions on.
 *
 * Resolves null when the tag is unavailable (hard ad-block / not yet loaded)
 * within `timeoutMs` — callers fall back to the synchronous cookie read, and
 * may report a "blocked" event so the irreducible floor is measurable.
 */
export function fetchClientIdViaTag(timeoutMs = 800): Promise<string | null> {
  return new Promise((resolve) => {
    const { counterId } = cfg()
    if (!counterId || typeof window === 'undefined' || typeof window.ym !== 'function') {
      resolve(null)
      return
    }
    let done = false
    const timer = setTimeout(() => {
      if (!done) {
        done = true
        resolve(null)
      }
    }, timeoutMs)
    try {
      // getClientID is callback-based; the existing void `ym()` wrapper can't
      // surface the return, so call window.ym directly here.
      window.ym(counterId, 'getClientID', (cid: string) => {
        if (done) return
        done = true
        clearTimeout(timer)
        resolve(cid || null)
      })
    } catch {
      clearTimeout(timer)
      resolve(null)
    }
  })
}

const UID_CACHE_KEY = 'pwa-metrica-uid'

// Restore the cached Metrica UID once at startup (identifies returning users
// before any events fire). Call from app bootstrap. Native activates the SDK here.
export function initMetrica(): void {
  if (isNative) {
    if (NATIVE_METRIKA_ID) MetricaNative.init({ apiKey: NATIVE_METRIKA_ID }).catch(() => {})
    return
  }
  const { counterId } = cfg()
  const cached = localStorage.getItem(UID_CACHE_KEY)
  if (counterId && cached) {
    try {
      ym(counterId, 'setUserID', cached)
    } catch {
      /* ignore */
    }
  }
}

// Drop the Metrica identity (on logout) so the next account on this device isn't
// attributed to the previous user. Mirrors the original which removed
// 'pwa-metrica-uid' in logout() (auth.js:459).
export function resetUserId(): void {
  if (isNative) {
    MetricaNative.resetUserId().catch(() => {})
    return
  }
  try {
    localStorage.removeItem(UID_CACHE_KEY)
  } catch {
    /* ignore */
  }
}

export function reachGoal(target: string, params: Record<string, unknown> = {}): void {
  if (isNative) {
    MetricaNative.trackEvent({ name: target, params }).catch(() => {})
    return
  }
  const { counterId } = cfg()
  if (!counterId) return
  try {
    ym(counterId, 'reachGoal', target, params)
    if (cfg().product !== 'break_up') ym(counterId, 'hit', '/event/' + target, { params })
  } catch {
    /* ignore */
  }
}

export function setUserId(userId: string): void {
  // The hash is shared web+native so a user is identified identically across platforms.
  const { counterId, salt = '' } = cfg()
  try {
    crypto.subtle
      .digest('SHA-256', new TextEncoder().encode(userId + salt))
      .then((buf) => {
        const hash = Array.from(new Uint8Array(buf))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')
          .slice(0, 16)
        if (isNative) {
          MetricaNative.setUserId({ userId: hash }).catch(() => {})
          return
        }
        if (!counterId) return
        try {
          ym(counterId, 'setUserID', hash)
          localStorage.setItem(UID_CACHE_KEY, hash)
        } catch {
          /* ignore */
        }
      })
      .catch(() => {})
  } catch {
    /* crypto.subtle unavailable */
  }
}
